import { useMemo, useEffect, useCallback, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { doc, onSnapshot } from 'firebase/firestore';
import { useFirebaseUser } from '../context/FirebaseUserContext';
import { GET_USER_PROFILE } from '../services/membershipApi/apollo/queries';
import { db } from '../services/firebase';
import type { UserProfile } from '../services/membershipApi/profile';

type GetUserProfileData = { getUserProfile: UserProfile | null };
type GetUserProfileVars = { id: string };

interface UseUserProfileResult {
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  /**
   * Firestore-backed `photoName` is still being resolved.
   * This is separate from the GraphQL profile `loading` flag and is used to
   * prevent UI flashes (e.g., showing initials) before we know whether a user
   * actually has a photoName in Firestore.
   */
  photoNameLoading: boolean;
  hasProfile: boolean;
  hasActiveMembership: boolean;
  refetch: () => Promise<void>;
}

export const useUserProfile = (): UseUserProfileResult => {
  const { user } = useFirebaseUser();
  
  // Firestore state (for fields not in DataConnect)
  const [firestorePhotoName, setFirestorePhotoName] = useState<string | null>(null);
  const [photoNameLoading, setPhotoNameLoading] = useState(() => !!user?.uid);
  const [firestorePendingEmail, setFirestorePendingEmail] = useState<string | null>(null);

  const { data, loading, error: apolloError, refetch } = useQuery<GetUserProfileData, GetUserProfileVars>(
    GET_USER_PROFILE,
    {
      variables: { id: user?.uid ?? '' },
      skip: !user?.uid,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    }
  );

  // Subscribe to Firestore Users document for fields not in DataConnect
  // Also use Firestore as a signal to refetch DataConnect when email changes
  useEffect(() => {
    if (!user?.uid) {
      setFirestorePhotoName(null);
      setPhotoNameLoading(false);
      setFirestorePendingEmail(null);
      return;
    }

    setPhotoNameLoading(true);
    const userDocRef = doc(db, 'Users', user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setFirestorePhotoName(userData?.photoName ?? null);
          setFirestorePendingEmail(userData?.pendingEmail ?? null);

          // If Firestore email differs from DataConnect email, refetch DataConnect
          // This handles email changes made by Cloud Functions
          const firestoreEmail = userData?.email;
          const dataConnectEmail = data?.getUserProfile?.email;
          
          if (firestoreEmail && dataConnectEmail && firestoreEmail !== dataConnectEmail) {
            refetch();
          }
        } else {
          setFirestorePhotoName(null);
          setFirestorePendingEmail(null);
        }
        setPhotoNameLoading(false);
      },
      (error) => {
        // Silently handle errors - these fields are optional
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to fetch Firestore data:', error);
        }
        setFirestorePhotoName(null);
        setPhotoNameLoading(false);
        setFirestorePendingEmail(null);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, data?.getUserProfile?.email, refetch]);

  useEffect(() => {
    if (!apolloError) return;
    if (process.env.NODE_ENV === 'development') {
      console.error('GraphQL error', {
        message: apolloError.message,
        errors: CombinedGraphQLErrors.is(apolloError) ? apolloError.errors : apolloError,
      });
    } else {
      console.error('Profile query failed');
    }
  }, [apolloError]);

  // Merge Firestore data with GraphQL profile
  const profile = useMemo(() => {
    const graphqlProfile = data?.getUserProfile ?? null;
    if (!graphqlProfile) return null;
    
    // photoName and pendingEmail are stored in Firestore Users collection
    // photoName: set by mobile app
    // pendingEmail: set during email change flow (awaiting verification)
    return {
      ...graphqlProfile,
      photoName: firestorePhotoName ?? undefined,
      pendingEmail: firestorePendingEmail ?? undefined,
    };
  }, [data?.getUserProfile, firestorePhotoName, firestorePendingEmail]);

  const error = useMemo(() => {
    if (!apolloError) return null;
    if (CombinedGraphQLErrors.is(apolloError)) {
      const code = (apolloError.errors[0]?.extensions as { code?: string })?.code;
      if (code === 'USER_NOT_FOUND') return null;
    }
    if (/User not found|Failed to load user dashboard/i.test(apolloError.message)) return null;
    return apolloError.message;
  }, [apolloError]);

  const wrappedRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const hasActiveMembership = useMemo(
    () => !!profile?.memberships?.some((m) => m?.status === 'active'),
    [profile?.memberships]
  );

  return useMemo(
    () => ({
      profile,
      loading,
      error,
      photoNameLoading,
      hasProfile: !!profile,
      hasActiveMembership,
      refetch: wrappedRefetch,
    }),
    [profile, loading, error, photoNameLoading, hasActiveMembership, wrappedRefetch]
  );
};
